import { API_BASE } from "./auth.js";

/**
 * The subset of the v7 report contract this UI renders.
 *
 * Deliberately a hand-written subset rather than an import of the backend's
 * contract.ts: the Site is a separate deployable, and typing it against the
 * server's internals would make every backend refactor a frontend build
 * failure. `site/integration-contract.json` is the agreement between them.
 */
export type Reported<T> =
  | { state: "ok"; value: T }
  | { state: "missing"; note?: string }
  | { state: "unavailable"; note?: string };

export interface InvoiceLine {
  displayName?: string | null;
  item: string;
  sku: string | null;
  productFamily: string | null;
  quantity: number | null;
  amount: number | null;
  /** Recorded NetSuite rate in transaction currency; never inferred from the total. */
  unitPrice?: number | null;
  lineId?: string | null;
  lineNumber?: number | null;
  description?: string | null;
  currency?: string | null;
  /** NetSuite transactionline.taxline flag; absent in older snapshots. */
  isTaxLine?: boolean;
}

export interface ProductSoldRollup {
  displayName?: string | null;
  productFamily: string | null;
  item: string;
  sku: string | null;
  totalQuantity: number;
  /** Distinct invoice-line quantities, sorted numerically; absent in older snapshots. */
  uniqueQuantities?: number[];
  netAmount: number;
  transactionCount: number;
}

export interface IssueComment {
  author: string | null;
  created: string | null;
  body: string;
}

export interface Issue {
  created?: string | null; updated?: string | null;
  key: string;
  url: string;
  summary: string;
  status: string;
  ageDays: number;
  daysSinceUpdate: number;
  issueType?: string | null;
  priority?: string | null;
  description?: string;
  comments?: IssueComment[];
}

export interface SummaryBullet {
  code: string;
  severity: "info" | "warning" | "critical";
  text: string;
}

export interface Finding {
  code: string;
  severity: "info" | "warning" | "critical";
  title: string;
  detail: string;
  links: Array<{ label: string; url: string }>;
}

export interface Kpi {
  key: string;
  label: string;
  value: Reported<number | string>;
  unit: "currency" | "days" | "percent" | "count" | "none";
  currency: string | null;
  emphasis: "normal" | "warning" | "critical";
}

export interface CustomerReportResponse {
  status: "ready" | "partial" | "pending" | "duplicate_confirmation" | "not_found" | "error";
  message: string | null;
  reportId: string | null;
  generatedAt?: string | null;
  sourceTrail?: Array<{ source: string; status: string; recordsRead: number; retrievedAt: string; error: string | null }>;
  customer: { name: string; customerNumber?: string | null; city?: string | null; country?: string | null; netsuiteUrl?: string | null; hubspotUrl?: string | null; hubspotCompanyId?: string | null; companyOwner?: { name: string | null } | null } | null;
  candidates: Array<{
    source: "netsuite" | "hubspot";
    id: string;
    name: string;
    customerNumber: string | null;
    matchReasons: string[];
  }>;
  resolutionToken: string | null;
  caveats: Array<{ code: string; severity: "info" | "warning"; message: string }>;
  report: {
    summary: { headline: string; bullets: SummaryBullet[]; generatedBy: string; modelId?: string; aiStatus?: string };
    kpis: Kpi[];
    needsAttention: Finding[];
    productsSold: ProductSoldRollup[];
    deals?: {
      totalCount: number; openCount: number; wonCount: number; lostCount: number;
      totalValue: Reported<number>; currency: Reported<string>; truncated: boolean;
      missingAmountCount: number; activityFieldUsed: string;
      recent: Array<{ id: string; name: string; url: string | null; amount: Reported<number>; currency: string | null; stageLabel: string; pipelineLabel: string | null; closeDate: string | null; daysSinceActivity: number | null; isStale: boolean; isClosed?: boolean; isWon?: boolean; isLost?: boolean; owner: { name: string | null } | null }>;
    };
    creditMemos?: Array<{ id: string; tranId: string; date: string | null; statusLabel: string; total: Reported<number>; currency: string | null; url: string | null }>;
    financials: {
      currency: Reported<string>;
      netRevenue?: Reported<number>; ytdNetRevenue?: Reported<number>; outstanding?: Reported<number>; overdueAmount?: Reported<number>; overdueCount?: number; invoiceCount?: number;
      revenueTrend: Array<{ month: string; grossInvoiced: number; netRevenue: number }>;
      revenueTrendTruncated: boolean;
    };
    invoices: Array<{
      id?: string;
      url?: string | null;
      currency?: string | null;
      tranId: string;
      date: string | null;
      dueDate?: string | null;
      statusLabel: string;
      total: Reported<number>;
      isOverdue: boolean;
      daysPastDue: number | null;
      lines: InvoiceLine[];
    }>;
    contacts: Array<{
      id: string;
      name: string;
      url: string | null;
      jobTitle: string | null;
      jobFunction: string | null;
    }>;
    jira: {
      openIssues?: Issue[]; matchStrategy?: string; matchedOrganizations?: Array<{ id: string; name: string }>; closedCountIsApproximate?: boolean;
      openCount?: Reported<number>; totalCount?: Reported<number>; countIsApproximate?: boolean;
      byProject?: Array<{ key: string; name: string; url: string; openCount: number; totalCount: number }>;
      oldIssues: Issue[];
      staleIssues: Issue[];
      resolutionBreachedIssues: Issue[];
    };
  } | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * The ID token is the bearer, not the access token.
 *
 * Two reasons, and both bite silently if ignored. The gateway's JWT authorizer
 * validates `aud` against the client id, which is true of a Keycloak ID token
 * and not of its access token (`aud: account`). And authorization here rests
 * entirely on the `email` claim, which the access token does not carry.
 */
export async function fetchReport(
  customerName: string,
  idToken: string | null,
  extra: { resolutionToken?: string; selection?: Array<{ source: string; id: string }> } = {},
): Promise<CustomerReportResponse> {
  const res = await fetch(`${API_BASE}/api/customer-report`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ customerName, ...extra }),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // Keep the status-based message.
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as CustomerReportResponse;
}

export function readReported<T>(r: Reported<T> | undefined): T | null {
  return r && r.state === "ok" ? r.value : null;
}

/** The note explaining why a value is absent — never render a bare blank. */
export function absenceNote<T>(r: Reported<T> | undefined): string | null {
  if (!r || r.state === "ok") return null;
  return r.note ?? (r.state === "unavailable" ? "Could not be retrieved" : "Not recorded");
}

export function money(amount: number, currency: string | null): string {
  const formatted = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return currency ? `${currency} ${formatted}` : formatted;
}

export interface UsageEntry {
  id: string; reportId: string; customer: string; requestedBy: string; at: string; model: string; outcome: string;
  inputTokens: number | null; cachedTokens: number | null; outputTokens: number | null; estimatedUsd: number | null;
  durationMs: number; reportDurationMs: number; errorCode: string | null;
}
export interface ReportingUser {
  email: string; requests: number; reports: number; partial: number; errors: number; customers: number; aiSummaries: number; aiTracked: number; knownEstimatedUsd: number; unpricedAi: number; averageMs: number | null; lastActiveAt: string | null;
}
export interface ReportingAnalytics {
  period: { days: number; start: string; end: string; dayTimezone: string };
  totals: { requests: number; reports: number; partial: number; errors: number; noMatches: number; selections: number; activeUsers: number; customers: number; partialRate: number | null; errorRate: number | null; medianMs: number | null; p95Ms: number | null; timingSamples: number; aiSuccessRate: number | null; aiCompleted: number; aiPending: number; aiTimeouts: number; costCoverage: number | null };
  users: ReportingUser[]; daily: Array<{date:string;requests:number;reports:number;estimatedUsd:number}>; truncated: boolean;
  coverage: { oldestSavedAt: string | null; aiWithoutSavedReport: number };
}
export interface UsageDashboard {
  analytics: ReportingAnalytics;
  summary: { reports: number; generated: number; fallbacks: number; knownEstimatedUsd: number; unpricedReports: number; averageReportMs: number | null; averageMeasuredUsd: number | null; inputTokens: number; outputTokens: number; truncated: boolean; priceSource: string };
  entries: UsageEntry[]; readers?: string[];
}
export async function reportingRequest<T>(path: string, token: string | null, emails?: string[]): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: emails ? "POST" : "GET", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(emails ? { "Content-Type": "application/json" } : {}) }, ...(emails ? { body: JSON.stringify({ emails }) } : {}) });
  const body = await res.json();
  if (!res.ok) throw new Error(body.error ?? "Reporting is temporarily unavailable.");
  return body as T;
}

export async function exportUsage(token: string | null, days: number): Promise<void> {
  const response = await fetch(`${API_BASE}/api/ai-usage?days=${days}&format=csv`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error("Usage export is unavailable or you do not have access.");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a"); link.href = url; link.download = `customer-report-usage-${days}d.csv`; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
